// Chowa シリーズ・モデル定義
// バックエンドから動的に取得するが、色情報は静的に保持

import { getChowaSeries, getUniqueModelsBySeriesName } from '../api_config';

// シリーズの色定義（静的）
export const CHOWA_SERIES_COLORS = {
  HV: '#3b82f6', // ブルー
  SR: '#f97316', // オレンジ  
  ZV: '#9333ea', // パープル
  // 新しいシリーズが追加された場合のデフォルト色
  default: '#6b7280' // グレー
};

// 静的なシリーズ定義（後方互換性のため）
export const CHOWA_SERIES = {
  HV: {
    name: 'HVシリーズ',
    color: CHOWA_SERIES_COLORS.HV,
    models: [] // 動的に取得
  },
  SR: {
    name: 'SRシリーズ', 
    color: CHOWA_SERIES_COLORS.SR,
    models: [] // 動的に取得
  },
  ZV: {
    name: 'ZVシリーズ',
    color: CHOWA_SERIES_COLORS.ZV,
    models: [] // 動的に取得
  }
};

// ==============================================
// 動的データ取得関数群
// ==============================================

/**
 * バックエンドからシリーズ一覧を取得
 * @returns {Promise<Array>} シリーズ一覧
 */
export const fetchChowaSeriesList = async () => {
  try {
    const response = await getChowaSeries();
    
    if (response.data?.status === 'success' && response.data?.data?.series) {
      return response.data.data.series.map(series => ({
        id: series.id,
        name: series.name,
        key: extractSeriesKey(series.name),
        color: getSeriesColorByName(series.name)
      }));
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching series list:', error);
    return [];
  }
};

/**
 * 指定シリーズの機種・排ガス規制の組み合わせを取得
 * @param {string} seriesName - シリーズ名（例: 'SR'）
 * @returns {Promise<Array>} 機種・排ガス規制の組み合わせ配列
 */
export const fetchModelsBySeries = async (seriesName) => {
  try {
    return await getUniqueModelsBySeriesName(seriesName);
  } catch (error) {
    console.error('Error fetching models by series:', error);
    return [];
  }
};

// ==============================================
// ユーティリティ関数群
// ==============================================

/**
 * シリーズ名からシリーズキーを抽出（HVシリーズ → HV）
 * @param {string} seriesName - シリーズ名
 * @returns {string} シリーズキー
 */
export const extractSeriesKey = (seriesName) => {
  // 'HVシリーズ' → 'HV', 'SRシリーズ' → 'SR' など
  const match = seriesName.match(/^([A-Z]+)/);
  return match ? match[1] : seriesName;
};

/**
 * シリーズ名から色を取得
 * @param {string} seriesName - シリーズ名
 * @returns {string} 色コード
 */
export const getSeriesColorByName = (seriesName) => {
  const key = extractSeriesKey(seriesName);
  return CHOWA_SERIES_COLORS[key] || CHOWA_SERIES_COLORS.default;
};

// ==============================================
// 後方互換性のための関数群
// ==============================================

// 全シリーズの配列（サイドバー用）
export const SERIES_LIST = Object.keys(CHOWA_SERIES).map(seriesKey => ({
  key: seriesKey,
  ...CHOWA_SERIES[seriesKey]
}));

// シリーズキーからモデル一覧を取得（動的版）
export const getModelsBySeries = async (seriesKey) => {
  return await fetchModelsBySeries(seriesKey);
};

// シリーズキーから色を取得
export const getSeriesColor = (seriesKey) => {
  return CHOWA_SERIES_COLORS[seriesKey] || CHOWA_SERIES_COLORS.default;
};

// モデル名から色を取得
export const getModelColor = (modelName) => {
  // モデル名から基本シリーズを推定（例: SR-60(Ⅲ) → SR）
  const match = modelName.match(/^([A-Z]+)/);
  const seriesKey = match ? match[1] : null;
  return getSeriesColor(seriesKey);
}; 